import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TestCategoryService} from '../../../../../services/testCategory.service';
import TestCategoryModel from '../../../../../services/testCategory.model';
import * as moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

/**
 * TestCategoryComponent
 *
 * This component is responsible for managing Test Categories within the Test Configurations module.
 * It provides a user interface for administrators to view, create, update, and search for test categories.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all test categories.
 * - Provide search functionality to filter categories by name or abbreviation.
 * - Manage the creation of new test categories with validation.
 * - Handle updates to existing test categories.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update test categories.
 * - **Real-time Search**: Filter list by category name or abbreviation.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures category names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all test categories from `TestCategoryService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateTestCategory` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-test-category
 * @example
 * <app-test-category></app-test-category>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-test-category',
  templateUrl: './test-category.component.html',
  styleUrls: ['./test-category.component.scss']
})
export class TestCategoryComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched test categories from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {TestCategoryModel[]}
   * @default []
   */
  public testCategories: TestCategoryModel[] = [];

  /**
   * Array holding the currently displayed test categories.
   * This list is populated by the BatchLoader (subset of testCategories or filteredTestCategories).
   * @type {TestCategoryModel[]}
   * @default []
   */
  public testCategoriesList: TestCategoryModel[] = [];

  /**
   * Model for the current test category being added or edited.
   * Bound to the form for data manipulation.
   * @type {TestCategoryModel}
   */
  public testCategory: TestCategoryModel = new TestCategoryModel();

  /**
   * Array holding test categories that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {TestCategoryModel[]}
   * @default []
   */
  public filteredTestCategories: TestCategoryModel[] = [];

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Reactive form group for test category details (name, abbreviation, description).
   * @type {FormGroup}
   */
  registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  submitted = false;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public testCategoryDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Active page number (not actively used in current logic but declared).
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for TestCategoryComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {TestCategoryService} testCategoryService - Service for Test Category API operations.
   * @service TestCategoryService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public testCategoryService: TestCategoryService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.testCategory = new TestCategoryModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all test categories from the backend.
   * 5. Formats the 'updatedAt' date for each category.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.testCategories = [];
    this.testCategoriesList = [];
    this.filteredTestCategories = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.testCategories = await this.testCategoryService.getAllTestCategory();
      if (this.testCategories.length > 0) {
        this.testCategories.forEach(eachTestCategory => {
          eachTestCategory.updatedAt = JSON.parse(JSON.stringify(moment(eachTestCategory.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 100);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredTestCategories : this.testCategories,
      (updatedList) => {
        this.testCategoriesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.testCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method testCategoryFormValidation
   * @returns {AbstractControl} Form controls
   */
  get testCategoryFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the test category list based on the search query.
   *
   * - Filters by name (case-insensitive).
   * - Updates `filteredTestCategories`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredTestCategories`, `testCategoriesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.testCategories.filter(route =>
        route.name?.toLowerCase().includes(query)
      )
      : this.testCategories;
    this.filteredTestCategories = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.testCategoriesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.testCategory = new TestCategoryModel();
    this.submitButton = false;
    this.updateButton = false;
    this.testCategoryDoubleClick = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Test Category form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateTestCategory
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateTestCategory() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.testCategoryDoubleClick = true;
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      formData.created_name = localStorage.getItem('LoginUserName');
      formData.updated_name = localStorage.getItem('LoginUserName');
      if (this.submitButton) {
        await this.testCategoryService.create(formData);
        this.toastrService.success('Test category created successfully', '', {
          timeOut: 3000
        });
      } else {
        const updateTestCategory = {...formData}
        delete updateTestCategory.id;
        await this.testCategoryService.updateOneByID(this.testCategory.id, updateTestCategory);
        this.toastrService.success('Test category updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.testCategoryDoubleClick = false;
    }
  }

  /**
   * Retrieves a Test Category by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getTestCategoryById
   * @param {string} id - The ID of the test category to edit.
   * @returns {void}
   */
  public getTestCategoryById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.testCategoryDoubleClick = false;
    const foundTestCategory = this.testCategoriesList.find(uom => uom?.id === id);
    this.testCategory = foundTestCategory ? {...foundTestCategory} : new TestCategoryModel(); // clone object
    if (this.testCategory) {
      this.registerForm.patchValue({
        name: this.testCategory.name,
        abbreviation: this.testCategory.abbreviation,
        description: this.testCategory.description
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
